import { html, nothing, render } from 'lit/html.js';
import { currentLang, setCurrentLang, applyLang, t } from './i18n';
import { currentUser, setUserTimezone, userTimezone } from './state';
import { api, apiGetDedupe } from './api';
import { showToast } from './toast';
import { loadAllGoals } from './upload';
import type { ProfilePreferences, ProfileWhere, AccountingWhere } from './types';
import { getErrorMessage } from './types';
import {
  accountingWhereFromProfileValue,
  buildAccountingWhere,
  draftsEqual,
  resolveProfileDiscardStrategy,
  resolveProfileSaveCompletion,
} from './profile-draft-logic';
import { openInviteAcceptFromHash } from './navigation';
import { setAccountName } from './app-nav';
import {
  accountingWithoutSystems,
  aiWorkDraftFromView,
  aiWorkPreferenceFromDraft,
  EMPTY_AI_WORK_DRAFT,
  parseAiWorkDraft,
  serializeAiWorkDraft,
  type AiWorkVerbView,
  type AiWorkView,
} from './ai-work-draft-logic';

export interface ProfileDraft {
  profDisplayName: string;
  profEmail: string;
  profDisplayNames: string;
  profLocation: string;
  /** #2817: 拠点2 and which base is used on a weekday daytime. */
  profLocation2: string;
  profWeekdayDaytimeLocation: string;
  profTimezone: string;
  profLanguage: string;
  prefCommExternal: string;
  prefCommInternal: string;
  prefTaskGranularity: string;
  prefTravelMode: string;
  prefMeetingDefaultAttendance: string;
  prefEventDefaultAttendance: string;
  prefUnreadDeadline: string;
  prefAccPaymentsExec: string;
  prefAccPaymentsSystem: string;
  prefAccInvoicesExec: string;
  prefAccInvoicesSystem: string;
  prefAccExpensesExec: string;
  prefAccExpensesSystem: string;
  /** #2732 stage 4: `AiWorkDraft` as JSON, so drafts compare as strings. */
  prefAiWork: string;
}

const profileState = {
  loading: false,
  saving: false,
  error: '',
  success: '',
  timezoneOptions: [] as Array<{ value: string; label: string }>,
};

export type ProfileSaveResult =
  | { status: 'idle'; message: '' }
  | { status: 'saved' | 'error'; message: string };

export interface ProfileSnapshot {
  baseline: ProfileDraft;
  current: ProfileDraft;
  attempt: ProfileDraft | null;
  retry: ProfileDraft | null;
  isSaving: boolean;
  isDirty: boolean;
  loading: boolean;
  result: ProfileSaveResult;
  timezoneOptions: Array<{ value: string; label: string }>;
}

let _aiWorkVerbViews: AiWorkVerbView[] = [];

function initialProfileDraft(): ProfileDraft {
  return {
    profDisplayName: '',
    profEmail: '',
    profDisplayNames: '',
    profLocation: '',
    profLocation2: '',
    profWeekdayDaytimeLocation: 'first',
    profTimezone: userTimezone,
    profLanguage: currentLang,
    prefCommExternal: 'formal',
    prefCommInternal: 'auto',
    prefTaskGranularity: 'normal',
    prefTravelMode: 'train',
    prefMeetingDefaultAttendance: 'auto',
    prefEventDefaultAttendance: 'auto',
    prefUnreadDeadline: 'same_day',
    prefAccPaymentsExec: '',
    prefAccPaymentsSystem: '',
    prefAccInvoicesExec: '',
    prefAccInvoicesSystem: '',
    prefAccExpensesExec: '',
    prefAccExpensesSystem: '',
    prefAiWork: serializeAiWorkDraft(EMPTY_AI_WORK_DRAFT),
  };
}

let profileDraft = initialProfileDraft();
let profileBaseline = { ...profileDraft };
let profileAttempt: ProfileDraft | null = null;
let profileRetry: ProfileDraft | null = null;
let profileResult: ProfileSaveResult = { status: 'idle', message: '' };
let resultTimer: ReturnType<typeof setTimeout> | null = null;
const profileSubscribers = new Set<(snapshot: ProfileSnapshot) => void>();

function $input(id: string): HTMLInputElement { return document.getElementById(id) as HTMLInputElement; }
function $select(id: string): HTMLSelectElement { return document.getElementById(id) as HTMLSelectElement; }
function $el(id: string): HTMLElement { return document.getElementById(id) as HTMLElement; }

export function getProfileSnapshot(): ProfileSnapshot {
  return {
    baseline: { ...profileBaseline },
    current: { ...profileDraft },
    attempt: profileAttempt ? { ...profileAttempt } : null,
    retry: profileRetry ? { ...profileRetry } : null,
    isSaving: profileState.saving,
    isDirty: profileRetry !== null || !draftsEqual(profileDraft, profileBaseline),
    loading: profileState.loading,
    result: { ...profileResult },
    timezoneOptions: [...profileState.timezoneOptions],
  };
}

function notifyProfileSubscribers() {
  const snapshot = getProfileSnapshot();
  profileSubscribers.forEach((subscriber) => subscriber(snapshot));
}

export function subscribeProfile(subscriber: (snapshot: ProfileSnapshot) => void): () => void {
  profileSubscribers.add(subscriber);
  subscriber(getProfileSnapshot());
  return () => profileSubscribers.delete(subscriber);
}

export function updateProfileField<K extends keyof ProfileDraft>(key: K, value: ProfileDraft[K]) {
  if (profileDraft[key] === value) return;
  profileDraft = { ...profileDraft, [key]: value };
  profileResult = { status: 'idle', message: '' };
  profileState.success = '';
  profileState.error = '';
  notifyProfileSubscribers();
  if ((AI_WORK_DRAFT_KEYS as readonly string[]).includes(key)) void saveAiWorkNow();
}

/**
 * #2762: what 「AIに任せる業務」 edits is saved as soon as it changes, so a
 * choice on a card never waits for the save bar. Only these preference keys
 * are sent; the server merges them into the stored preferences.
 */
const AI_WORK_DRAFT_KEYS = [
  'prefAiWork', 'prefCommExternal', 'prefCommInternal', 'prefMeetingDefaultAttendance',
  'prefEventDefaultAttendance', 'prefTravelMode', 'prefAccPaymentsExec', 'prefAccInvoicesExec', 'prefAccExpensesExec',
] as const satisfies readonly (keyof ProfileDraft)[];
let aiWorkSaving = false;
let aiWorkSaveAgain = false;

async function saveAiWorkNow() {
  if (aiWorkSaving) {
    aiWorkSaveAgain = true;
    return;
  }
  aiWorkSaving = true;
  const attemptDraft = { ...profileDraft };
  try {
    const all = buildProfilePreferences(attemptDraft);
    const preferences: ProfilePreferences = {
      ai_work: all.ai_work,
      comm_external: all.comm_external,
      comm_internal: all.comm_internal,
      meeting_default_attendance: all.meeting_default_attendance,
      event_default_attendance: all.event_default_attendance,
      travel_mode: all.travel_mode,
      where: all.where,
    };
    await api('/api/profile/preferences', { method: 'PATCH', json: { preferences } });
    const saved: Partial<ProfileDraft> = {};
    for (const key of AI_WORK_DRAFT_KEYS) saved[key] = attemptDraft[key];
    profileBaseline = { ...profileBaseline, ...saved };
    if (!aiWorkSaveAgain) showToast(t('saved') as string, 'info', undefined);
  } catch {
    showToast(t('aiWorkSaveFailed') as string, 'error', undefined);
  } finally {
    aiWorkSaving = false;
    notifyProfileSubscribers();
    if (aiWorkSaveAgain) {
      aiWorkSaveAgain = false;
      void saveAiWorkNow();
    }
  }
}

export function discardProfileChanges() {
  profileDraft = { ...profileBaseline };
  profileRetry = null;
  profileResult = { status: 'idle', message: '' };
  notifyProfileSubscribers();
}

function pickEnum<T extends string>(value: string, allowed: readonly T[], fallback: T): T {
  return (allowed as readonly string[]).includes(value) ? (value as T) : fallback;
}

function readMeetingDefaultAttendance(prefs: ProfilePreferences): 'in_person' | 'online' | 'auto' {
  if (prefs.meeting_default_attendance === 'in_person' || prefs.meeting_default_attendance === 'online') {
    return prefs.meeting_default_attendance;
  }
  const legacy = prefs.meeting as Record<string, unknown> | undefined;
  if (legacy?.default_format === '現地参加') return 'in_person';
  if (legacy?.default_format === 'オンライン参加') return 'online';
  return 'auto';
}

function readEventDefaultAttendance(prefs: ProfilePreferences): 'in_person' | 'online' | 'auto' {
  if (prefs.event_default_attendance === 'in_person' || prefs.event_default_attendance === 'online') {
    return prefs.event_default_attendance;
  }
  return 'auto';
}

export function initTimezoneSelect() {
  if (profileState.timezoneOptions.length > 0) return;
  const tzList = [
    'Pacific/Honolulu', 'America/Anchorage', 'America/Los_Angeles', 'America/Denver',
    'America/Chicago', 'America/New_York', 'America/Sao_Paulo',
    'Europe/London', 'Europe/Paris', 'Europe/Berlin', 'Europe/Moscow',
    'Asia/Dubai', 'Asia/Kolkata', 'Asia/Bangkok', 'Asia/Singapore',
    'Asia/Shanghai', 'Asia/Seoul', 'Asia/Tokyo',
    'Australia/Sydney', 'Pacific/Auckland',
  ];
  const browserTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const all = tzList.includes(browserTz) ? tzList : [browserTz, ...tzList];
  profileState.timezoneOptions = all.map((tz) => {
    let label = tz;
    try {
      const offset = new Intl.DateTimeFormat('en', { timeZone: tz, timeZoneName: 'shortOffset' }).formatToParts(new Date()).find(p => p.type === 'timeZoneName')?.value || '';
      label = `${tz.replace(/_/g,' ')} (${offset})`;
    } catch {}
    return { value: tz, label };
  });
}

async function loadProfileDraft(options: { preserveOnError?: boolean } = {}): Promise<boolean> {
  profileState.loading = true;
  profileState.error = '';
  profileState.success = '';
  initTimezoneSelect();
  notifyProfileSubscribers();

  const nextDraft = {
    ...initialProfileDraft(),
    profDisplayName: currentUser?.name || '',
    profEmail: currentUser?.email || '',
  };
  let nextAiWorkVerbViews: AiWorkVerbView[] = [];
  let loaded = false;

  try {
    const data = await apiGetDedupe('/api/profile') as any;
    const p = data.profile;
    if (p) {
      nextDraft.profDisplayNames = (p.display_names || []).join(', ');
      nextDraft.profLocation = p.location || '';
      const prefs = (p.preferences || {}) as ProfilePreferences;
      const where = prefs.where || {};
      nextDraft.prefCommExternal = prefs.comm_external || 'formal';
      nextDraft.prefCommInternal = prefs.comm_internal || 'auto';
      nextDraft.prefTaskGranularity = prefs.task_granularity || 'normal';
      nextDraft.prefTravelMode = prefs.travel_mode || 'train';
      nextDraft.profLocation2 = prefs.second_location || '';
      nextDraft.profWeekdayDaytimeLocation = prefs.weekday_daytime_location === 'second' ? 'second' : 'first';
      nextDraft.prefMeetingDefaultAttendance = readMeetingDefaultAttendance(prefs);
      nextDraft.prefEventDefaultAttendance = readEventDefaultAttendance(prefs);
      // #3064: a `none` saved before reads as the default; every goal gets a provisional deadline.
      nextDraft.prefUnreadDeadline = prefs.unread_deadline_default === 'next_business_day' ? 'next_business_day' : 'same_day';
      const acct = accountingWhereFromProfileValue(where.accounting) as AccountingWhere | null;
      nextDraft.prefAccPaymentsExec = acct?.payments?.executor || '';
      nextDraft.prefAccPaymentsSystem = acct?.payments?.system || '';
      nextDraft.prefAccInvoicesExec = acct?.invoices?.executor || '';
      nextDraft.prefAccInvoicesSystem = acct?.invoices?.system || '';
      nextDraft.prefAccExpensesExec = acct?.expenses?.executor || '';
      nextDraft.prefAccExpensesSystem = acct?.expenses?.system || '';
      const aiWorkView = data.ai_work_view as AiWorkView | undefined;
      nextAiWorkVerbViews = aiWorkView?.verbs ?? [];
      nextDraft.prefAiWork = serializeAiWorkDraft(aiWorkDraftFromView(aiWorkView));
      nextDraft.profTimezone = prefs.timezone || userTimezone;
      nextDraft.profLanguage = p.language || currentLang;
    } else {
      nextDraft.profTimezone = userTimezone;
      nextDraft.profLanguage = currentLang;
      nextDraft.prefMeetingDefaultAttendance = 'auto';
      nextDraft.prefEventDefaultAttendance = 'auto';
    }
    loaded = true;
  } catch (err) {
    void err;
    if (options.preserveOnError) {
      const message = t('settingsDiscardReloadError') as string;
      profileState.loading = false;
      profileState.error = message;
      profileResult = { status: 'error', message };
      notifyProfileSubscribers();
      return false;
    }
    nextDraft.profTimezone = userTimezone;
    nextDraft.profLanguage = currentLang;
    nextDraft.prefMeetingDefaultAttendance = 'auto';
    nextDraft.prefEventDefaultAttendance = 'auto';
  }

  _aiWorkVerbViews = nextAiWorkVerbViews;
  profileDraft = nextDraft;
  profileBaseline = { ...nextDraft };
  profileAttempt = null;
  profileRetry = null;
  profileResult = { status: 'idle', message: '' };
  profileState.error = '';
  profileState.success = '';
  profileState.loading = false;
  notifyProfileSubscribers();
  return loaded;
}

// Settings-page (#1473) mounts <zen-profile-sections> directly and manages its own
// open/visibility state. This loads the shared profile draft for that surface.
export async function loadProfileForSettings() {
  await loadProfileDraft();
}

export async function discardProfileChangesForSettings(): Promise<boolean> {
  if (resolveProfileDiscardStrategy(profileRetry !== null) === 'reset-local') {
    discardProfileChanges();
    return true;
  }
  return loadProfileDraft({ preserveOnError: true });
}

export function showOnboarding() {
  $el('onbStepName').style.display = '';
  $el('onbStepConnect').style.display = 'none';
  $el('onboardingModal').style.display = 'flex';
  const email = currentUser?.email || '';
  const el = document.getElementById('onbLoginEmail');
  if (el) el.textContent = email ? t('loginStatus', { email }) as string : '';
}

/**
 * Step 1 of 2 (#2811): only the display name. Location, travel mode and writing
 * style are asked later, the first time the work needs them (#2817, #2821). The
 * step marks onboarding complete, so returning from a provider's OAuth page in
 * step 2 lands in the app rather than here.
 */
export async function submitOnboarding() {
  const displayName = $input('onbDisplayName').value.trim();
  if (!displayName) {
    $el('onboardingError').textContent = t('displayNameRequired') as string;
    return;
  }
  const btn = $input('onboardingSubmitBtn');
  btn.disabled = true;
  btn.innerHTML = '<span class="spinner spinner-white"></span>';
  $el('onboardingError').textContent = '';

  try {
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const language = navigator.language.startsWith('ja') ? 'ja' : 'en';
    await api('/api/profile', {
      method: 'PATCH',
      json: {
        display_name: displayName,
        display_names: [displayName],
        language,
        preferences: { timezone },
      },
    });
    await api('/api/profile/sync-rules', {
      method: 'PATCH',
      json: {
        // Who does what starts from the defaults in AIに任せる業務 (#2762), not a
        // choice made here.
        sync_rules: {
          onboarding_completed_at: new Date().toISOString(),
        },
      },
    });
    setUserTimezone(timezone);
    setCurrentLang(language);
    localStorage.setItem('zenmetry_lang', currentLang);
    applyLang();
    if (currentUser) {
      currentUser.name = displayName;
      setAccountName(displayName);
    }
    $el('onbStepName').style.display = 'none';
    $el('onbStepConnect').style.display = '';
    render(html`<zen-my-connections-section></zen-my-connections-section>`, $el('onbConnections'));
  } catch (err: unknown) {
    $el('onboardingError').textContent = getErrorMessage(err);
  } finally {
    btn.disabled = false;
    btn.textContent = t('onboardingNextBtn') as string;
  }
}

/** Step 2 of 2: connecting leaves for the provider; this closes the modal without connecting. */
export async function finishOnboarding() {
  render(nothing, $el('onbConnections'));
  $el('onboardingModal').style.display = 'none';
  await loadAllGoals();
  await openInviteAcceptFromHash();
}

export function csvToArray(str: string): string[] {
  return str.split(/[,、]/).map(s => s.trim()).filter(Boolean);
}

export function getAiWorkVerbViews(): AiWorkVerbView[] {
  return [..._aiWorkVerbViews];
}

function publishTimedResult(result: ProfileSaveResult) {
  profileResult = result;
  if (resultTimer) clearTimeout(resultTimer);
  resultTimer = setTimeout(() => {
    profileResult = { status: 'idle', message: '' };
    profileState.success = '';
    profileState.error = '';
    notifyProfileSubscribers();
  }, 6000);
}

function buildProfilePreferences(attemptDraft: ProfileDraft): ProfilePreferences {
  return {
    timezone: attemptDraft.profTimezone,
    comm_external: pickEnum(attemptDraft.prefCommExternal, ['formal', 'business_casual', 'casual', 'auto'] as const, 'formal'),
    comm_internal: pickEnum(attemptDraft.prefCommInternal, ['formal', 'casual', 'auto'] as const, 'auto'),
    task_granularity: pickEnum(attemptDraft.prefTaskGranularity, ['fine', 'coarse', 'normal'] as const, 'normal'),
    travel_mode: pickEnum(attemptDraft.prefTravelMode, ['train', 'car', 'plane', 'undecided', 'bike'] as const, 'train'),
    second_location: attemptDraft.profLocation2.trim() || null,
    weekday_daytime_location: pickEnum(attemptDraft.profWeekdayDaytimeLocation, ['first', 'second'] as const, 'first'),
    meeting_default_attendance: pickEnum(
      attemptDraft.prefMeetingDefaultAttendance,
      ['in_person', 'online', 'auto'] as const,
      'auto',
    ),
    event_default_attendance: pickEnum(
      attemptDraft.prefEventDefaultAttendance,
      ['in_person', 'online', 'auto'] as const,
      'auto',
    ),
    unread_deadline_default: pickEnum(
      attemptDraft.prefUnreadDeadline,
      ['same_day', 'next_business_day'] as const,
      'same_day',
    ),
    // #2732 stage 4: services live in the habit list; the old fields that
    // named one are cleared. #2762: who handles accounting is edited on the
    // 支払い・請求書・経費精算 cards, so it is read from the draft.
    where: {
      accounting: accountingWithoutSystems(
        buildAccountingWhere(attemptDraft) as Record<string, unknown> | null,
      ) as AccountingWhere | null,
      mail: null,
      schedule: null,
      chat: null,
    },
    ai_work: aiWorkPreferenceFromDraft(parseAiWorkDraft(attemptDraft.prefAiWork), _aiWorkVerbViews),
  };
}

export async function saveProfile() {
  if (profileState.saving) return;
  const attemptDraft = { ...profileDraft };
  profileAttempt = attemptDraft;
  profileRetry = null;
  profileState.saving = true;
  profileState.error = '';
  profileState.success = '';
  notifyProfileSubscribers();

  try {
    const display_name = attemptDraft.profDisplayName.trim();
    const display_names = csvToArray(attemptDraft.profDisplayNames);
    const location = attemptDraft.profLocation.trim() || null;
    const selectedTz = attemptDraft.profTimezone;
    const selectedLang = attemptDraft.profLanguage;
    const preferences = buildProfilePreferences(attemptDraft);

    await api('/api/profile', {
      method: 'PATCH',
      json: {
        display_name,
        display_names,
        location,
        language: selectedLang,
      },
    });

    await api('/api/profile/preferences', {
      method: 'PATCH',
      json: { preferences },
    });
    const completion = resolveProfileSaveCompletion(profileBaseline, profileDraft, attemptDraft, true);
    profileBaseline = completion.baseline;
    profileRetry = completion.retry;
    profileState.success = t('settingsProfileSaved') as string;
    setUserTimezone(selectedTz);

    if (display_name && currentUser) {
      currentUser.name = display_name;
      setAccountName(display_name);
    }

    if (selectedLang !== currentLang) {
      setCurrentLang(selectedLang);
      localStorage.setItem('zenmetry_lang', currentLang);
      applyLang();
    }

    publishTimedResult({ status: 'saved', message: t('settingsProfileSaved') as string });
    showToast(t('saved') as string, 'info', undefined);
  } catch (err: unknown) {
    const completion = resolveProfileSaveCompletion(profileBaseline, profileDraft, attemptDraft, false);
    profileBaseline = completion.baseline;
    profileRetry = completion.retry;
    const partialSaveMessage = t('settingsProfileSavePartialError') as string;
    profileState.error = partialSaveMessage;
    publishTimedResult({ status: 'error', message: partialSaveMessage });
  } finally {
    profileState.saving = false;
    notifyProfileSubscribers();
  }
}
